import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect, useLocation, useHistory} from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import auth from '../../services/authentication';
import {StoreState} from '../../type.d';
import TrovaLogo from '../../components/TrovaLogo/TrovaLogo';
import signInMan from '../../../images/signin-man.png';
import * as Styled from './SignInPageStyles';
import queryString from 'query-string';
import Api from '../../api/Api';
import * as actionTypes from '../../store/actionTypes';
import useObservable from '../../hooks/useObservable';
import SubscriptionsStore from '../../xstores/subscriptions';
import Meta from '../../components/Meta/Meta';
import Box from '../../styled/Box';
import {Image} from '../../styled/Image';
import {H1, Paragraph} from '../../styled/Typography';
import LoginBlock from './LoginBlock';

const errMsg = {
	general: 'Something went wrong. Please try again!',
	notInvited: (
		<span>Thank you for your interest in Trova! Please sign up for our <a href="/signin-beta">public beta.</a></span>
	)
}

const SignInPage = () => {
	const [isRedirect, setRedirect] = useState(false);
	const [isRedirectToSearch, setRedirectToSearch] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [loginLoading, setLoginLoading] = useState(false);
	const [error, setError] = useState<any>('');
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();

	const {loginState} = useSelector( (rootReducer:StoreState) => rootReducer.reducer);
	const subscriptions = useObservable(SubscriptionsStore.current$, []);
	const loadingSubscriptions = useObservable(SubscriptionsStore.loading$, false);
	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		if (searchParams.loggedIn === 'false') {
			setError(errMsg.notInvited);
			history.replace({
				search: '',
			});
			return;
		}
		const loginUser = async () => {
			if (!searchParams.code) {
				if (searchParams.loggedIn) {
					setError(errMsg.general);
				}
				return;
			}
			try {
				setLoginLoading(true);
				const resp:any = await Api.getToken(searchParams.code.toString());
				auth.setToken(resp.data.token);
				setLoginLoading(false);
				dispatch({
					type: actionTypes.LOGIN_GOOGLE_SUCCESS,
					payload: resp.data,
				});
				SubscriptionsStore.getUserSubscriptions(true);
				history.push('/searchportal');
			} catch (err) {
				console.log(err);
				setError(errMsg.general);
				history.replace({
					search: '',
				});
				setLoginLoading(false);
			}
		}
		loginUser();
	}, [history, location.search, dispatch]);

	useEffect(() => {
		if((loginState.data && loginState.data.token) && (loginState.data && loginState.data.user) && auth.isAuthenticated() ){
			SubscriptionsStore.getUserSubscriptions();
			setLoading(false);
			setError('');
		}
		if (loginState.err) {
			// @ts-ignore
			if (loginState.err.msg.indexOf('not invited') > -1) {
				setError(errMsg.notInvited);
			} else {
				setError(errMsg.general);
			}
			setLoading(false);
		}
		return () => {
			setLoading(false);
			setRedirect(false);
		}
	}, [loginState, dispatch]);

	useEffect(() => {
		if(auth.isAuthenticated() && !loadingSubscriptions && !subscriptions.length){
			setLoading(false);
			setRedirect(true);
		}
		else if(subscriptions.length){
			setLoading(false);
			setRedirectToSearch(true);
		}
		return () => {
			setRedirectToSearch(false);
			setRedirect(false);
		}
	}, [subscriptions, loadingSubscriptions])

	if(isRedirect){
		return <Redirect to ='/account' />;
	}
	else if(isRedirectToSearch){
		return <Redirect to ='/searchportal' />;
	}

	return (
		<Styled.SignInWrapper>
			<Meta page="signin" />
			<Box width="100%">
				<Styled.StyledLink to="/" style={{ display: 'block', margin: '20px 20px'}}>
					<TrovaLogo hasText />
				</Styled.StyledLink>
			</Box>
			<Box display={'flex'} justifyItems={'center'} flex={'1'}>
				<Styled.FormContainer
					borderRadius="24px"
					flex="1"
					ml={[ 3, 4, 4, 5, 5, 'auto']}
					mr={[ 3, 4, 4, 5, 5, 'auto']}
					mb='100px'
					maxWidth="1200px"
				>
					<Box
						className="d-none d-lg-flex leftBlock"
						bg="rgba(191, 207, 242, 0.32)"
						width={[0, 0, 1/2, 1/2, 3/5]}
						display={'flex'}
						alignItems={'center'}
						justifyContent={'center'}
						boxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
					>
						<Styled.Wrapper className="CombinedImages">
							<Image src={signInMan} alt='Signin' className='MansPhone' />
							<H1 weight={600} color={'primaryViolet'} mt={2}>
								Welcome to Trova!
							</H1>
							<Paragraph size="sm" color={'primaryViolet'}>
								Personal unified search service across different platforms to find your scattered information quickly.
							</Paragraph>
						</Styled.Wrapper>
					</Box>
					<Box
						p={'8% 32px'}
						display={'flex'}
						flex={1}
						flexDirection={'column'}
						alignItems={'center'}
						justifyContent={'space-between'}
					>
						{isLoading ?
							<Spinner animation="border" variant="primary" /> :
							<LoginBlock loading={loginLoading} error={error} />
						}
						<Box maxWidth={400}>
							<Paragraph size={'xs'} color={'primaryViolet'}>
								* Signing up via Google or Microsoft account allows you to choose
								the information you want to share with us. All your data
								stays in your Google or Microsoft account not in Trova.
							</Paragraph>
							<Paragraph size={'xs'} color={'primaryViolet'}>
								By connecting to Trova, you agree to our&nbsp;<br/>
								<Styled.StyledLink to='/tos'>Terms of Use</Styled.StyledLink> and &nbsp;
								<Styled.StyledLink to='/privacy'>Privacy Policy.</Styled.StyledLink>
							</Paragraph>
						</Box>
					</Box>
				</Styled.FormContainer>
			</Box>
		</Styled.SignInWrapper>
	)
}

export default SignInPage;